import { addDays, isAfter, isBefore, isSameDay } from "date-fns";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  getCountries,
  getDraftYear,
  getGameCancellationReasons,
  getGameParticipationLookup,
  getLocales,
  getMediaFileSources,
  getMedicalAssetTypes,
  getMedicalBodyParts,
  getPregameActivities,
  getSchoolTypes,
  getStates,
  getSystemSetting,
} from "../clients/DPLClient";
import {
  ANDROID_APP_URL_SETTING,
  APPLE_APP_URL_SETTING,
  COLLEGE_RECOVERY_END_DATE,
  COLLEGE_RECOVERY_START_DATE,
  HIGH_SCHOOL_RECOVERY_END_DATE,
  HIGH_SCHOOL_RECOVERY_START_DATE,
  LAST_DAY_OF_DRAFT,
  SCHOOL_LOCK_END_DATE,
  SCHOOL_LOCK_START_DATE,
} from "../constants/SystemSettingsConstants";
import { MediaFileSourceDTO } from "../types/Box";
import { Country } from "../types/Country";
import { DraftYearDTO } from "../types/DraftYearDTO";
import { LocaleDTO } from "../types/Language";
import { MedicalAssetTypeDTO, MedicalBodyPartDTO } from "../types/Medical";
import {
  GameCancellationReasonDTO,
  GameParticipationDTO,
  PregameActivityDTO,
  SchoolTypeData,
  SystemSetting,
} from "../types/Metadata";
import { State, StateOption } from "../types/States";
import { ValueDisplay } from "../types/ValueDisplay";
import { useAuth } from "./AuthContext";

type MetadataContextType = {
  draftYear?: number;
  lastDayOfDraft: Date | null;
  locales: LocaleDTO[];
  schoolLockStartDate: Date | null;
  schoolLockEndDate: Date | null;
  pregameActivityOptions: ValueDisplay[];
  gameParticipationOptions: ValueDisplay[];
  gameCancellationOptions: ValueDisplay[];
  androidAppURL: string;
  iosAppURL: string;
  mediaUploadTypeOptions: MediaFileSourceDTO[];
  bodyPartOptions: ValueDisplay[];
  assetTypeOptions: ValueDisplay[];
  stateOptions: StateOption[];
  countries: Country[];
  schoolTypes: SchoolTypeData[];
  isHighSchoolRecoveryPeriodActive: boolean;
  highSchoolRecoveryPeriodfunctionalityAvailable: Date | null;
  isCollegeRecoveryPeriodActive: boolean;
  collegeRecoveryPeriodfunctionalityAvailable: Date | null;
};

const MetadataContext = React.createContext<MetadataContextType | undefined>(undefined);

const MetadataProvider: React.FC = ({ children }) => {
  const { prospectId } = useAuth();
  const [draftYear, setDraftYear] = useState<number | undefined>();
  const [locales, setLocales] = useState<LocaleDTO[]>([]);
  const [schoolLockStartDate, setSchoolLockStartDate] = useState<Date | null>(null);
  const [schoolLockEndDate, setSchoolLockEndDate] = useState<Date | null>(null);
  const [pregameActivityOptions, setPregameActivityOptions] = useState<ValueDisplay[]>([]);
  const [gameParticipationOptions, setGameParticipationOptions] = useState<ValueDisplay[]>([]);
  const [gameCancellationOptions, setGameCancellationOptions] = useState<ValueDisplay[]>([]);
  const [androidAppURL, setAndroidAppURL] = useState<string>("");
  const [iosAppURL, setIosAppURL] = useState<string>("");
  const [mediaUploadTypeOptions, setMediaUploadTypeOptions] = useState<MediaFileSourceDTO[]>([]);
  const [bodyPartOptions, setBodyPartOptions] = useState<ValueDisplay[]>([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState<ValueDisplay[]>([]);
  const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [schoolTypes, setSchoolTypes] = useState<SchoolTypeData[]>([]);
  const [lastDayOfDraft, setLastDayOfDraft] = useState<Date | null>(null);
  const [highSchoolRecoveryPeriodStartDate, setHighSchoolRecoveryPeriodStartDate] = useState<Date | null>(null);
  const [highSchoolRecoveryPeriodEndDate, setHighSchoolRecoveryPeriodEndDate] = useState<Date | null>(null);
  const [collegeRecoveryPeriodStartDate, setCollegeRecoveryPeriodStartDate] = useState<Date | null>(null);
  const [collegeRecoveryPeriodEndDate, setCollegeRecoveryPeriodEndDate] = useState<Date | null>(null);

  const isHighSchoolRecoveryPeriodActive = useMemo(() => {
    if (!highSchoolRecoveryPeriodStartDate || !highSchoolRecoveryPeriodEndDate) {
      return false;
    }
    const isSameDayOrAfterStartDate =
      isAfter(new Date(), highSchoolRecoveryPeriodStartDate) ||
      isSameDay(new Date(), highSchoolRecoveryPeriodStartDate);
    const isSameDayOrBeforeEndDate =
      isBefore(new Date(), highSchoolRecoveryPeriodEndDate) || isSameDay(new Date(), highSchoolRecoveryPeriodEndDate);
    return isSameDayOrAfterStartDate && isSameDayOrBeforeEndDate;
  }, [highSchoolRecoveryPeriodStartDate, highSchoolRecoveryPeriodEndDate]);

  const isCollegeRecoveryPeriodActive = useMemo(() => {
    if (!collegeRecoveryPeriodStartDate || !collegeRecoveryPeriodEndDate) {
      return false;
    }
    const isSameDayOrAfterStartDate =
      isAfter(new Date(), collegeRecoveryPeriodStartDate) || isSameDay(new Date(), collegeRecoveryPeriodStartDate);
    const isSameDayOrBeforeEndDate =
      isBefore(new Date(), collegeRecoveryPeriodEndDate) || isSameDay(new Date(), collegeRecoveryPeriodEndDate);
    return isSameDayOrAfterStartDate && isSameDayOrBeforeEndDate;
  }, [collegeRecoveryPeriodStartDate, collegeRecoveryPeriodEndDate]);

  const highSchoolRecoveryPeriodfunctionalityAvailable = useMemo(() => {
    if (!highSchoolRecoveryPeriodEndDate) {
      return null;
    }
    return addDays(highSchoolRecoveryPeriodEndDate, 1);
  }, [highSchoolRecoveryPeriodEndDate]);

  const collegeRecoveryPeriodfunctionalityAvailable = useMemo(() => {
    if (!collegeRecoveryPeriodEndDate) {
      return null;
    }
    return addDays(collegeRecoveryPeriodEndDate, 1);
  }, [collegeRecoveryPeriodEndDate]);

  const getSystemSettingDate = async (setting: string): Promise<Date | null> => {
    try {
      const systemSetting = await getSystemSetting(setting);
      if (!systemSetting) {
        return null;
      }
      // parse the date string properly to avoid timezone issues
      // split the date string and create a date in local timezone
      const [year, month, day] = systemSetting.val.split("-").map(Number);
      return new Date(year, month - 1, day);
    } catch (error) {
      console.error(`Failed to load ${setting} system setting:`, error);
      throw error;
    }
  };

  const getLastDayOfDraft = async () => {
    const lastDayOfDraft = await getSystemSettingDate(LAST_DAY_OF_DRAFT);
    setLastDayOfDraft(lastDayOfDraft);
  };

  const getHighSchoolRecoveryPeriodStartDate = async () => {
    const highSchoolRecoveryPeriodStartDate = await getSystemSettingDate(HIGH_SCHOOL_RECOVERY_START_DATE);
    setHighSchoolRecoveryPeriodStartDate(highSchoolRecoveryPeriodStartDate);
  };

  const getHighSchoolRecoveryPeriodEndDate = async () => {
    const highSchoolRecoveryPeriodEndDate = await getSystemSettingDate(HIGH_SCHOOL_RECOVERY_END_DATE);
    setHighSchoolRecoveryPeriodEndDate(highSchoolRecoveryPeriodEndDate);
  };

  const getCollegeRecoveryPeriodStartDate = async () => {
    const collegeRecoveryPeriodStartDate = await getSystemSettingDate(COLLEGE_RECOVERY_START_DATE);
    setCollegeRecoveryPeriodStartDate(collegeRecoveryPeriodStartDate);
  };

  const getCollegeRecoveryPeriodEndDate = async () => {
    const collegeRecoveryPeriodEndDate = await getSystemSettingDate(COLLEGE_RECOVERY_END_DATE);
    setCollegeRecoveryPeriodEndDate(collegeRecoveryPeriodEndDate);
  };

  const getDraftYearData = async () => {
    const result: DraftYearDTO = await getDraftYear();
    setDraftYear(result.year);
  };

  const getLocaleData = async () => {
    const result: LocaleDTO[] = await getLocales();
    setLocales(result);
  };

  const getSchoolLockDates = async () => {
    const startResult: SystemSetting = await getSystemSetting(SCHOOL_LOCK_START_DATE);
    const endResult: SystemSetting = await getSystemSetting(SCHOOL_LOCK_END_DATE);

    const startDate = new Date(startResult.val);
    const endDate = new Date(endResult.val);

    setSchoolLockStartDate(startDate);
    setSchoolLockEndDate(endDate);
  };

  const getAppURLs = async () => {
    const android: SystemSetting = await getSystemSetting(ANDROID_APP_URL_SETTING);
    const ios: SystemSetting = await getSystemSetting(APPLE_APP_URL_SETTING);
    setAndroidAppURL(android.val);
    setIosAppURL(ios.val);
  };

  const getPregameActivityData = async () => {
    const results: PregameActivityDTO[] = await getPregameActivities();
    const options: ValueDisplay[] = results.map((dto) => ({
      value: dto.id,
      display: dto.name,
    }));
    setPregameActivityOptions(options);
  };

  const getGameParticipationData = async () => {
    const results: GameParticipationDTO[] = await getGameParticipationLookup();
    const options: ValueDisplay[] = results.map((dto) => ({
      value: dto.id,
      display: dto.name,
    }));
    setGameParticipationOptions(options);
  };

  const getGameCancellationData = async () => {
    const results: GameCancellationReasonDTO[] = await getGameCancellationReasons();
    const options: ValueDisplay[] = results.map((dto) => ({
      value: dto.id,
      display: dto.name,
    }));
    setGameCancellationOptions(options);
  };

  const getMediaUploadTypeOptions = async () => {
    const results: MediaFileSourceDTO[] = await getMediaFileSources();
    setMediaUploadTypeOptions(results);
  };

  const getBodyPartOptions = async () => {
    const res: MedicalBodyPartDTO[] = await getMedicalBodyParts();
    const options: ValueDisplay[] = res.map((dto) => ({
      value: dto.id,
      display: dto.name,
    }));
    setBodyPartOptions(options);
  };

  const getAssetTypeOptions = async () => {
    const res: MedicalAssetTypeDTO[] = await getMedicalAssetTypes();
    const options: ValueDisplay[] = res.map((dto) => ({
      value: dto.id,
      display: dto.name,
    }));
    setAssetTypeOptions(options);
  };

  const getStatesData = async () => {
    // US and Canada states/provinces
    const results: State[] = [...(await getStates(1)), ...(await getStates(2))];
    const options: StateOption[] = results.map((state: State) => ({
      display: state.name,
      value: state,
    }));
    setStateOptions(options);
  };

  const getCountryData = async () => {
    const results: Country[] = await getCountries();
    setCountries(results);
  };

  const getSchoolTypesData = async () => {
    const result: SchoolTypeData[] = await getSchoolTypes();
    setSchoolTypes(result);
  };

  useEffect(() => {
    getPregameActivityData();
    getGameParticipationData();
    getGameCancellationData();
    getMediaUploadTypeOptions();
    getBodyPartOptions();
    getAssetTypeOptions();
    getStatesData();
    getCountryData();
  }, []);

  // authenticated calls
  useEffect(() => {
    if (prospectId) {
      getDraftYearData();
      getLastDayOfDraft();
      getLocaleData();
      getSchoolLockDates();
      getAppURLs();
      getSchoolTypesData();
      getHighSchoolRecoveryPeriodStartDate();
      getHighSchoolRecoveryPeriodEndDate();
      getCollegeRecoveryPeriodStartDate();
      getCollegeRecoveryPeriodEndDate();
    }
  }, [prospectId]);

  return (
    <MetadataContext.Provider
      value={{
        draftYear,
        lastDayOfDraft,
        locales,
        schoolLockStartDate,
        schoolLockEndDate,
        pregameActivityOptions,
        gameParticipationOptions,
        gameCancellationOptions,
        androidAppURL,
        iosAppURL,
        mediaUploadTypeOptions,
        bodyPartOptions,
        assetTypeOptions,
        stateOptions,
        countries,
        schoolTypes,
        isHighSchoolRecoveryPeriodActive,
        highSchoolRecoveryPeriodfunctionalityAvailable,
        isCollegeRecoveryPeriodActive,
        collegeRecoveryPeriodfunctionalityAvailable,
      }}
    >
      {children}
    </MetadataContext.Provider>
  );
};

const useMetadata = (): MetadataContextType => {
  const context: MetadataContextType | undefined = useContext(MetadataContext);
  if (context === undefined) {
    throw new Error(`useMetadata must be used within a MetadataProvider`);
  }
  return context;
};

export { MetadataProvider, useMetadata };
